import { Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";

// import BalanceCard from "./BalanceCard";
// import PerformanceChart from "./PerformanceChart";
import { createLeadApi } from "../../api/leads.api";
import { getUsersByCapabilitiesApi } from "../../api/users.api";
import { getDashboardApi } from "../../api/dashboard.api";
import Loader from "../Dashboard/Loader";
// import ConversionDroppedLeadList from "../Reports/Conversions/ConversionsDroppedLeadList";
import { Stack } from "@mui/system";
import HeaderTopCards from "./HeaderTopCards";
import FunnelOverview from "./FunnelOverview";
import TodaysPriorityPanel from "./TodaysPriorityPanel";
import PerformanceSnapshot from "./PerformanceSnapshot";
import AddLeadModal from "../Leads_V2/Renderer/AddLeadModal";
import socket from "../../utils/socket";
import { useSettings } from "../../context/SettingsContext";
import { AuthContext } from "../../context/AuthContext";
import { useContext } from "react";

const cap = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Compute dashboard stats from leads data (like LeadsLayout)
const computeDashboardStats = (leads) => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Total leads
  const totalLeads = leads.length;

  // Active consults (leads with bookings today or future)
  const activeConsults = leads.filter((l) => {
    if (!l.bookings || l.bookings.length === 0) return false;
    return l.bookings.some((b) => {
      if (b.status === "Cancelled") return false;
      const bookingDate = new Date(b.date);
      return bookingDate >= today;
    });
  }).length;

  // Conversions (leads with converted/done status)
  const convertedLeads = leads.filter((l) =>
    ["converted", "done", "closed", "won"].includes(
      l.current_status?.key?.toLowerCase()
    )
  );

  // Conversion rate
  const convRate =
    totalLeads > 0
      ? ((convertedLeads.length / totalLeads) * 100).toFixed(1) + "%"
      : "0%";

  // Revenue (MTD) - sum of payments this month
  const revenueMTD = leads.reduce((sum, l) => {
    if (!l.payments || l.payments.length === 0) return sum;
    const monthPayments = l.payments.filter((p) => {
      const paymentDate = new Date(p.date || p.createdAt);
      return paymentDate >= startOfMonth && paymentDate <= now;
    });
    return sum + monthPayments.reduce((s, p) => s + (p.amount || 0), 0);
  }, 0);

  // Pending follow-ups (leads with followups due today or overdue)
  const pendingFollowups = leads.filter((l) => {
    if (!l.next_followup) return false;
    const followupDate = new Date(l.next_followup.date);
    return followupDate <= today && followupDate >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  }).length;

  // Recent conversions (last 5)
  const recent_conversions = convertedLeads
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 5);

  // Recent dropped (last 5)
  const recent_dropped = leads
    .filter((l) =>
      ["dropped", "lost", "cancelled"].includes(
        l.current_status?.key?.toLowerCase()
      )
    )
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 5);

  return {
    totalLeads,
    activeConsults,
    convRate,
    revenue: "₹" + (revenueMTD / 1000).toFixed(1) + "k",
    pendingFollowups,
    recent_conversions,
    recent_dropped,
    leads,
  };
};

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const myUserId = user?._id || "";
  const { services = [], lead_features } = useSettings();
  const [dashboard, setDashboard] = useState(null);
  const [apiStats, setApiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [leadManagers, setLeadManagers] = useState([]);
  const [leads, setLeads] = useState([]);
  const fetchedRef = useRef(false);
  const batchBufferRef = useRef([]);

  const sourcesFromSettings = useMemo(() => {
    const list = lead_features?.sources || [];
    return [
      "all",
      ...new Set(
        list
          .map((s) => (typeof s === "string" ? s : s?.key || s?.name))
          .filter(Boolean)
      ),
    ];
  }, [lead_features]);

  const treatments = useMemo(() => {
    const list = (services || []).filter((x) => x && x !== "—").map(cap);
    return ["all", ...new Set(list)];
  }, [services]);

  const telecallerOptions = useMemo(() => {
    const list = Array.isArray(leadManagers) ? leadManagers : [];
    return list
      .filter((u) => u && u._id && u.name)
      .map((u) => ({
        id: u._id,
        label: `${u.name}${u.team ? ` (${u.team})` : ""}`,
        name: u.name,
        email: u.email,
        team: u.team,
      }));
  }, [leadManagers]);

  // Fetch leads via socket — buffer batches, render on first batch
  const fetchLeads = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    setLeads([]);
    batchBufferRef.current = [];

    socket.off("leads-batch");
    socket.off("leads-complete");
    socket.off("leads-error");
    socket.off("connect");
    socket.off("connect_error");
    socket.offAny();

    let firstBatchRendered = false;

    // Use onAny to catch leads-batch — more reliable than socket.on after disconnect/reconnect
    socket.onAny((event, data) => {
      if (event === "leads-batch" && Array.isArray(data)) {
        batchBufferRef.current.push(...data);
        if (!firstBatchRendered) {
          firstBatchRendered = true;
          const snapshot = [...batchBufferRef.current];
          setLeads(snapshot);
          setDashboard(computeDashboardStats(snapshot));
          setLoading(false);
        }
      }

      if (event === "leads-complete") {
        const allLeads = [...batchBufferRef.current];
        setLeads(allLeads);
        setDashboard(computeDashboardStats(allLeads));
        fetchedRef.current = false;
      }

      if (event === "leads-error") {
        setLoading(false);
        fetchedRef.current = false;
      }
    });

    const emitFetch = () => {
      socket.emit("get-leads-batch", { batchSize: 100 });
    };

    if (socket.connected) {
      emitFetch();
    } else {
      socket.disconnect();
      socket.connect();
      socket.once("connect", emitFetch);
      socket.once("connect_error", () => {
        setLoading(false);
        fetchedRef.current = false;
      });
    }
  }, []);

  const fetchLeadManagers = async () => {
    try {
      const res = await getUsersByCapabilitiesApi(["lead_management"]);
      setLeadManagers(res?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch lead managers", err);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await getDashboardApi();
      setApiStats(res?.data?.data || null);
    } catch (err) {
      console.error("Failed to fetch dashboard stats", err);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchLeadManagers();
    fetchDashboardStats();

    // const interval = setInterval(fetchDashboardStats, 30000);

    return () => {
      // clearInterval(interval);
      socket.offAny();
      socket.off("leads-batch");
      socket.off("leads-complete");
      socket.off("leads-error");
      socket.off("connect");
      socket.off("connect_error");
      socket.disconnect();
      fetchedRef.current = false;
      batchBufferRef.current = [];
    };
  }, []);

  // Listen for lead events and update local state (realtime)
  useEffect(() => {
    socket.connect();

    const handleLeadCreated = (lead) => {
      setLeads((prev) => {
        const exists = prev.find((l) => l._id === lead._id);
        if (exists) return prev;
        const next = [lead, ...prev];
        setDashboard(computeDashboardStats(next));
        return next;
      });
      fetchDashboardStats();
    };

    const handleLeadUpdated = (lead) => {
      setLeads((prev) => {
        const next = prev.map((l) => (l._id === lead._id ? lead : l));
        setDashboard(computeDashboardStats(next));
        return next;
      });
      fetchDashboardStats();
    };

    const handleStatusUpdated = (lead) => {
      handleLeadUpdated(lead);
    };

    socket.on("lead-created", handleLeadCreated);
    socket.on("lead-updated", handleLeadUpdated);
    socket.on("lead-status-updated", handleStatusUpdated);
    socket.on("lead-followup-rescheduled", handleLeadUpdated);
    socket.on("lead-followup-completed", handleLeadUpdated);

    return () => {
      socket.off("lead-created", handleLeadCreated);
      socket.off("lead-updated", handleLeadUpdated);
      socket.off("lead-status-updated", handleStatusUpdated);
      socket.off("lead-followup-rescheduled", handleLeadUpdated);
      socket.off("lead-followup-completed", handleLeadUpdated);
    };
  }, []);

  const recentRows = useMemo(() => {
    if (!dashboard) return [];
    return [
      ...(dashboard.recent_conversions || []),
      ...(dashboard.recent_dropped || []),
    ];
  }, [dashboard]);

  if (loading) return <Loader />;

  const handleQuickAction = (action) => {
    if (action.label === "Add New Lead") {
      setAddLeadOpen(true);
    }
  };

  const handleSaveLead = async (data) => {
    const payload = {
      client_name: data.fullName?.trim(),
      contact: data.phone?.trim(),
      email: data.email?.trim(),
      assign_employee: data.telecaller || undefined,
      services: Array.isArray(data.treatmentTypes) ? data.treatmentTypes : [],
      custom_fields: {
        lead_source: data.source || undefined,
        city: data.location || undefined,
        message: data.notes || undefined,
      },
    };
    const res = await createLeadApi(payload);
    // Socket will handle the lead-created event and update state automatically
  };

  return (
    <Box sx={{ py: 2 }}>
      <Stack>
        {/* <BalanceCard stats={dashboard} /> */}

        {/* <PerformanceChart stats={dashboard} /> */}

        <HeaderTopCards stats={dashboard} apiStats={apiStats} loading={loading} onQuickAction={handleQuickAction} />
        <FunnelOverview apiStats={apiStats} loading={loading} />

        <TodaysPriorityPanel leads={leads} />
        <PerformanceSnapshot stats={dashboard} leads={leads} loading={loading} />

        {/*
        <Grid size={{ xs: 12 }}>
          <Box sx={{ minWidth: 0, overflowX: "auto" }}>
            <ConversionDroppedLeadList rows={recentRows} />
          </Box>
        </Grid> */}
      </Stack>

      <AddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        telecallers={telecallerOptions}
        treatments={treatments}
        sources={sourcesFromSettings}
        onSave={handleSaveLead}
      />
    </Box>
  );
}
